import InterviewSection from '@/components/job/InterviewSection';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { InterviewResponse, JobResponse, useApiClient } from '@/lib/api';
import { isActiveJob, sortJobsByPriorityAndRecency } from '@/lib/job-status';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export default function Interviews() {
  const { api } = useApiClient();
  const [jobs, setJobs] = useState<JobResponse[]>([]);
  const [interviews, setInterviews] = useState<InterviewResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'open' | 'graded'>('open');

  useEffect(() => {
    const load = async () => {
      try {
        const [jobData, interviewData] = await Promise.all([
          api.getJobs(),
          api.getInterviews(false),
        ]);
        const activeJobs = sortJobsByPriorityAndRecency(
          jobData.filter((job) => isActiveJob(job))
        );
        setJobs(activeJobs);
        setInterviews(interviewData);
      } catch (error) {
        console.error('Failed to load interviews page:', error);
        toast.error('Unable to load interviews right now.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [api]);

  const openInterviews = useMemo(
    () =>
      interviews.filter(
        (interview) => interview.status.toUpperCase() !== 'COMPLETE'
      ),
    [interviews]
  );

  const gradedInterviews = useMemo(
    () =>
      interviews.filter(
        (interview) => interview.status.toUpperCase() === 'COMPLETE'
      ),
    [interviews]
  );

  const visibleInterviews =
    activeTab === 'open' ? openInterviews : gradedInterviews;

  return (
    <DashboardLayout>
      {loading ? (
        <div className="p-8 text-center">Loading...</div>
      ) : (
        <InterviewSection
          interviews={visibleInterviews}
          availableJobs={jobs}
          onInterviewsUpdate={setInterviews}
          creationMode="select"
          emptyMessage={
            activeTab === 'open'
              ? 'No open interviews right now. Start a new one above.'
              : 'No graded interviews yet.'
          }
          listControls={
            <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setActiveTab('open')}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'open'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Open
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('graded')}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'graded'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Graded
              </button>
            </div>
          }
        />
      )}
    </DashboardLayout>
  );
}
